import { CmpPaymentRequestStateEnum } from "../../../graphql/types";

export type MemberInvoiceDeliveryEvidence = {
  id: string;
  lastPaymentRequestSentAtUtcMilli?: number | null;
  paymentRequestState?: CmpPaymentRequestStateEnum | null;
};

export type CreatedMemberInvoiceKind = "membership" | "add-on";

export type CreatedMemberInvoiceDeliveryTarget<
  Invoice extends MemberInvoiceDeliveryEvidence,
> = {
  invoice: Invoice;
  kind: CreatedMemberInvoiceKind;
};

export type CreatedMemberInvoiceDeliveryCompletion<
  Invoice extends MemberInvoiceDeliveryEvidence,
  DeliveryResult,
> = CreatedMemberInvoiceDeliveryTarget<Invoice> & {
  deliveryResult: DeliveryResult;
};

export type CreatedMemberInvoiceDeliveryFailure<
  Invoice extends MemberInvoiceDeliveryEvidence,
> = CreatedMemberInvoiceDeliveryTarget<Invoice> & {
  error: Error;
};

export type SendOnlyUnsentCreatedMemberInvoicesInput<
  Invoice extends MemberInvoiceDeliveryEvidence,
  DeliveryResult,
> = {
  invoices: ReadonlyArray<CreatedMemberInvoiceDeliveryTarget<Invoice>>;
  send: (
    target: CreatedMemberInvoiceDeliveryTarget<Invoice>
  ) => Promise<DeliveryResult>;
  getFailure: (
    target: CreatedMemberInvoiceDeliveryTarget<Invoice>,
    deliveryResult: DeliveryResult
  ) => Error;
  wasDeliveryConfirmed: (deliveryResult: DeliveryResult) => boolean;
};

export type SendOnlyUnsentCreatedMemberInvoicesResult<
  Invoice extends MemberInvoiceDeliveryEvidence,
  DeliveryResult,
> = {
  completed: Array<
    CreatedMemberInvoiceDeliveryCompletion<Invoice, DeliveryResult>
  >;
  failure?: CreatedMemberInvoiceDeliveryFailure<Invoice>;
};

export const wasMemberInvoicePaymentRequestSent = (
  invoice: MemberInvoiceDeliveryEvidence | null | undefined
) => invoice?.paymentRequestState === CmpPaymentRequestStateEnum.Sent;

/**
 * Reconciles the create-mutation record with the post-commit member-detail
 * read. A recorded SENT state wins even if an eventually-consistent read is
 * briefly older than the mutation's add-on auto-delivery result.
 */
export const mergeCommittedMemberInvoiceDelivery = <
  Invoice extends MemberInvoiceDeliveryEvidence,
>({
  committedInvoice,
  mutationInvoice,
}: {
  committedInvoice?: MemberInvoiceDeliveryEvidence | null;
  mutationInvoice: Invoice;
}): Invoice => {
  const sentEvidence = [committedInvoice, mutationInvoice].find(
    wasMemberInvoicePaymentRequestSent
  );

  return {
    ...mutationInvoice,
    ...(committedInvoice
      ? {
          lastPaymentRequestSentAtUtcMilli:
            committedInvoice.lastPaymentRequestSentAtUtcMilli,
          paymentRequestState: committedInvoice.paymentRequestState,
        }
      : {}),
    ...(sentEvidence
      ? {
          lastPaymentRequestSentAtUtcMilli:
            sentEvidence.lastPaymentRequestSentAtUtcMilli,
          paymentRequestState: CmpPaymentRequestStateEnum.Sent,
        }
      : {}),
  };
};

/**
 * Delivers only invoices without committed SENT evidence. Partial completion
 * is returned instead of thrown away so the UI can retain success for the
 * first invoice and retry only the still-unsent invoice.
 */
export const sendOnlyUnsentCreatedMemberInvoices = async <
  Invoice extends MemberInvoiceDeliveryEvidence,
  DeliveryResult,
>({
  getFailure,
  invoices,
  send,
  wasDeliveryConfirmed,
}: SendOnlyUnsentCreatedMemberInvoicesInput<
  Invoice,
  DeliveryResult
>): Promise<
  SendOnlyUnsentCreatedMemberInvoicesResult<Invoice, DeliveryResult>
> => {
  const completed: Array<
    CreatedMemberInvoiceDeliveryCompletion<Invoice, DeliveryResult>
  > = [];

  for (const target of invoices.filter(
    ({ invoice }) => !wasMemberInvoicePaymentRequestSent(invoice)
  )) {
    try {
      const deliveryResult = await send(target);

      if (!wasDeliveryConfirmed(deliveryResult)) {
        return {
          completed,
          failure: {
            ...target,
            error: getFailure(target, deliveryResult),
          },
        };
      }

      completed.push({ ...target, deliveryResult });
    } catch (error) {
      return {
        completed,
        failure: {
          ...target,
          error:
            error instanceof Error
              ? error
              : new Error("Invoice payment request could not be sent."),
        },
      };
    }
  }

  return { completed };
};

export const memberDeliveryRefreshWarning =
  "The email was sent, but the page could not show the latest details. Refresh the page to see them. Do not send the email again.";

export const committedMemberActionRefreshWarning =
  "Your change was saved, but the page could not show the latest details. Refresh the page to see them. Do not repeat the change.";

export const refreshAfterCommittedMemberAction = async (
  refresh: () => Promise<unknown>
): Promise<string | undefined> => {
  try {
    await refresh();
    return undefined;
  } catch {
    return committedMemberActionRefreshWarning;
  }
};

export type RunConfirmedMemberDeliveryInput<DeliveryResult> = {
  deliver: () => Promise<DeliveryResult>;
  getDeliveryFailure: (deliveryResult: DeliveryResult) => Error;
  onDeliveryConfirmed: (deliveryResult: DeliveryResult) => void;
  refresh: () => Promise<unknown>;
  wasDeliveryConfirmed: (deliveryResult: DeliveryResult) => boolean;
};

export type RunConfirmedMemberDeliveryResult<DeliveryResult> = {
  deliveryResult: DeliveryResult;
  refreshWarning?: string;
};

/**
 * The delivery transaction and the subsequent detail refresh are independent.
 * Once audited delivery is confirmed, refresh failure resolves with a warning
 * and can never be reclassified as a send failure or trigger another send.
 */
export const runConfirmedMemberDelivery = async <DeliveryResult>({
  deliver,
  getDeliveryFailure,
  onDeliveryConfirmed,
  refresh,
  wasDeliveryConfirmed,
}: RunConfirmedMemberDeliveryInput<DeliveryResult>): Promise<
  RunConfirmedMemberDeliveryResult<DeliveryResult>
> => {
  const deliveryResult = await deliver();

  if (!wasDeliveryConfirmed(deliveryResult)) {
    throw getDeliveryFailure(deliveryResult);
  }

  onDeliveryConfirmed(deliveryResult);

  try {
    await refresh();
    return { deliveryResult };
  } catch {
    return {
      deliveryResult,
      refreshWarning: memberDeliveryRefreshWarning,
    };
  }
};
